import { useState } from "react";
import "../../assets/css/SignupOtp/SignupOtp.css";
import emailjs from "emailjs-com";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import {
  BASE_URL,
  EMAIL_SERVICE_ID,
  EMAIL_TEMPLATE_ID,
  EMAIL_PUBLIC_KEY,
} from "../../store/config";

import ErrorModal from "../../components/common/ErrorModal";

function SignupOtp() {
  //---------------------------------------------------
  // STATE
  //---------------------------------------------------
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);

  const [error, setError] = useState("");
  const [showErrorModal, setShowErrorModal] = useState(false);

  const navigate = useNavigate();

  //---------------------------------------------------
  // ERROR HELPER
  //---------------------------------------------------
  const showError = (msg) => {
    setError(msg);
    setShowErrorModal(true);
  };

  //---------------------------------------------------
  // OTP GENERATE
  //---------------------------------------------------
  const generateOtp = () =>
    Math.floor(100000 + Math.random() * 900000).toString();

  //---------------------------------------------------
  // HASH OTP
  //---------------------------------------------------
  const hashOtp = async (otp) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(otp);
    const hashBuffer = await crypto.subtle.digest("SHA-256", data);

    return Array.from(new Uint8Array(hashBuffer))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  };

  //---------------------------------------------------
  // EMAIL VALIDATION
  //---------------------------------------------------
  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  //---------------------------------------------------
  // SEND EMAIL OTP
  //---------------------------------------------------
  const sendOtpEmail = async (otp) => {
    return emailjs.send(
      EMAIL_SERVICE_ID,
      EMAIL_TEMPLATE_ID,
      {
        email,
        passcode: otp,
      },
      EMAIL_PUBLIC_KEY
    );
  };

  //---------------------------------------------------
  // SEND OTP
  //---------------------------------------------------
  const handleSendOtp = async () => {
    if (!email) return showError("Email is required!");
    if (!isValidEmail(email)) return showError("Invalid email format!");

    try {
      const otpGenerated = generateOtp();
      const hashedOtp = await hashOtp(otpGenerated);

      sessionStorage.setItem("otpHash", hashedOtp);
      sessionStorage.setItem("otpExpiry", Date.now() + 2 * 60 * 1000);

      await sendOtpEmail(otpGenerated);

      setOtpSent(true);
    } catch (err) {
      console.error(err);
      showError("Failed to send OTP");
    }
  };

  //---------------------------------------------------
  // VERIFY OTP + CREATE USER
  //---------------------------------------------------
  const handleVerifyOtp = async () => {
    if (!otp) return showError("Please enter OTP!");

    const storedHash = sessionStorage.getItem("otpHash");
    const expiry = sessionStorage.getItem("otpExpiry");

    if (!storedHash || Date.now() > expiry) {
      return showError("OTP expired");
    }

    const enteredHash = await hashOtp(otp);

    if (enteredHash !== storedHash) {
      return showError("Invalid OTP");
    }

    try {
      // clear OTP storage
      sessionStorage.removeItem("otpHash");
      sessionStorage.removeItem("otpExpiry");

      // CREATE USER IN BACKEND
      const res = await axios.post(
        `${BASE_URL}api/auth/signup_otp`,
        { email }
      );

      const { access_token, is_first_user } = res.data;

      // IMPORTANT: store token properly (use SAME KEY as app)
      localStorage.setItem("access_token", access_token);
      localStorage.setItem("isLoggedIn", "true");
      localStorage.setItem(
        "first_time_user",
        is_first_user ? "true" : "false"
      );

      // REDIRECT FIX
      if (is_first_user) {
        navigate("/my-profile");
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      console.error(err);

      if (err.response?.status === 409) {
        showError("Email already registered");
      } else {
        showError("Signup failed");
      }
    }
  };

  //---------------------------------------------------
  // RESET
  //---------------------------------------------------
  const handleReEnterEmail = () => {
    setEmail("");
    setOtp("");
    setOtpSent(false);
    sessionStorage.clear();
  };

  //---------------------------------------------------
  // UI
  //---------------------------------------------------
  return (
    <div className="otp-card">
      <h2 className="otp-title">Sign Up with OTP</h2>

      {/* EMAIL */}
      <div className="otp-field">
        <input
          type="email"
          value={email}
          disabled={otpSent}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email address"
        />
      </div>

      {/* SEND OTP */}
      {!otpSent && (
        <button className="otp-btn" onClick={handleSendOtp}>
          Send OTP
        </button>
      )}

      {/* OTP INPUT */}
      {otpSent && (
        <>
          <div className="otp-field">
            <input
              type="text"
              value={otp}
              maxLength="6"
              onChange={(e) => setOtp(e.target.value)}
              placeholder="Enter OTP"
            />
          </div>

          <div className="otp-btn-container">
            <button
              className="otp-btn-reenter"
              onClick={handleVerifyOtp}
            >
              Verify OTP
            </button>

            <button
              className="otp-btn-reenter"
              onClick={handleReEnterEmail}
            >
              Re-enter Email
            </button>
          </div>
        </>
      )}

      {/* ERROR MODAL */}
      <ErrorModal
        show={showErrorModal}
        message={error}
        onClose={() => {
          setShowErrorModal(false);
          setError("");
        }}
      />
    </div>
  );
}

export default SignupOtp;